import { useCallback } from "react"
import type { FC, ReactElement } from "react"

import { Loadable, RootLoaderFallback } from "@/3514/components"
import {
    CoachingSessionsEntity,
    ECoachingSessionsApiMode,
    type TCoachingSessionsEntityProps
} from "@/3514/entities/coaching-sessions"
import { useCoachingSessionsAnimationsReset } from "@/3514/hooks"
import { type TParticipantByIdQuery, useParticipantByIdQuery } from "@/3514/queries"
import { EUserRole, type IUserParticipantModel } from "@/3514/types"
import type { TEmptyCallback } from "@/shared/types/functions"

import classes from "./coach.group-participant.styles"
import { type TCoachGroupParticipantSharedTabProps as TProps } from "./coach.group-participant.ui.types"

const seenAnimationsPerParticipant: Map<number, boolean> = new Map()

const CoachGroupParticipantSessionsTab: FC<TProps> = ({ participantId }: TProps): ReactElement => {
    useCoachingSessionsAnimationsReset(seenAnimationsPerParticipant, participantId)

    const handleEntityMount: TEmptyCallback = useCallback(
        (): void => void seenAnimationsPerParticipant.set(participantId, true),
        [participantId]
    )

    const { data: participant, isFetching: isParticipantFetching }: TParticipantByIdQuery = useParticipantByIdQuery({
        userId: participantId
    })

    const props: TCoachingSessionsEntityProps = {
        apiMode: ECoachingSessionsApiMode.Individual,
        user: isParticipantFetching ? ({ id: participantId } as IUserParticipantModel) : participant,
        onMount: handleEntityMount,
        sessionInfoViewerRole: EUserRole.Coach,
        withSwappableSessions: false,
        withCancellableSessions: true,
        withAttendanceMarkableSessions: true,
        withPostSessionModalOpeners: false,
        withGiftSessions: true,
        wrapperClassName: classes.sessionsTab.container()
    }

    return (
        <Loadable<TCoachingSessionsEntityProps>
            loadingFallback={<RootLoaderFallback top="25%" xsTop="45%" />}
            component={CoachingSessionsEntity}
            componentProps={props}
        />
    )
}

CoachGroupParticipantSessionsTab.displayName = "CoachGroupParticipantSessionsTab"

export default CoachGroupParticipantSessionsTab
